import React, { useEffect, useId, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import {
    PREVIEW_LOCK_NOTE_ID,
    usePreviewLock,
} from '@/shared/hooks/usePreviewLock';
import { candidacyService } from '@/shared/services/candidacyService';
import { positionService } from '@/shared/services/positionService';

const LOAD_ERROR = "The open positions couldn't be loaded.";
const ADD_ERROR = "The student couldn't be added. Try again.";

export const positionLabel = (position) =>
    `${position.subject} ${position.courseNumber} section ${position.section}`;

const optionText = (position) =>
    `${positionLabel(position)}, ${position.instructorName || 'no instructor listed'} (${position.openSlots} open)`;

/**
 * Picks an open position for one pool member and adds them to it. The server
 * makes every check an offer makes, so a position that filled since this list
 * loaded is refused with its reason, shown here. `onAdded(position)` is called
 * after the student is added.
 */
export default function AddToPositionDialog({
    member,
    program,
    term,
    onAdded,
    onHide,
}) {
    const id = useId();
    const locked = usePreviewLock();
    const [positions, setPositions] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [chosen, setChosen] = useState('');
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState('');
    const show = Boolean(member);

    const loadPositions = async (isActive = () => true) => {
        setPositions(null);
        setLoadFailed(false);
        try {
            const answer = await positionService.listPositions({
                program,
                term,
            });
            if (!isActive()) return;
            setPositions(
                (answer.positions || []).filter(
                    (p) => p.status === 'open' && p.openSlots > 0
                )
            );
        } catch (_err) {
            if (isActive()) setLoadFailed(true);
        }
    };

    useEffect(() => {
        if (!show) return undefined;
        let active = true;
        setChosen('');
        setRefusal('');
        setBusy(false);
        loadPositions(() => active);
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, program, term]);

    const titleId = `${id}-title`;
    const selectId = `${id}-position`;
    const position = (positions || []).find((p) => p.applicationId === chosen);
    const blocked = busy || locked || !position;

    const confirm = async () => {
        if (blocked) return;
        setBusy(true);
        setRefusal('');
        try {
            await candidacyService.addToPosition(
                member.id,
                position.applicationId
            );
            onAdded(position);
        } catch (err) {
            setRefusal(err.response?.data?.message || ADD_ERROR);
            setBusy(false);
        }
    };

    let body;
    if (loadFailed) {
        body = (
            <Alert variant="danger" className="mb-0">
                <p className="mb-2">{LOAD_ERROR}</p>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => loadPositions()}
                >
                    Retry
                </Button>
            </Alert>
        );
    } else if (positions === null) {
        body = (
            <div role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading open positions...
            </div>
        );
    } else if (positions.length === 0) {
        body = <p className="mb-0">No position has an open place right now.</p>;
    } else {
        body = (
            <Form.Group controlId={selectId}>
                <Form.Label>Position</Form.Label>
                <Form.Select
                    value={chosen}
                    onChange={(event) => setChosen(event.target.value)}
                    disabled={busy}
                    aria-describedby={locked ? PREVIEW_LOCK_NOTE_ID : undefined}
                >
                    <option value="">Choose a position</option>
                    {positions.map((p) => (
                        <option key={p.applicationId} value={p.applicationId}>
                            {optionText(p)}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>
        );
    }

    return (
        <Modal
            show={show}
            onHide={() => {
                if (!busy) onHide();
            }}
            animation={false}
            restoreFocus
            aria-labelledby={titleId}
        >
            <Modal.Header closeButton={!busy}>
                <Modal.Title as="h2" className="h5" id={titleId}>
                    {`Add ${member?.student?.name || 'this student'} to a position`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    The student answered Yes to being considered for any open
                    position. They join the position&apos;s applicants at the
                    Applied stage, marked as added by the program. Nothing is
                    offered and no place is held.
                </p>
                {body}
                {refusal && (
                    <Alert variant="danger" className="mt-3 mb-0" role="alert">
                        {refusal}
                    </Alert>
                )}
                <PreviewLockNote locked={locked} />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={onHide}
                    disabled={busy}
                >
                    Cancel
                </Button>
                <Button variant="primary" onClick={confirm} disabled={blocked}>
                    {busy ? 'Adding...' : 'Add to position'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
